import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../firebase/AuthContext';
import { apiService } from '../services/api';
import { trackFeedbackSubmit } from '../services/analytics';

const FeedbackForm = ({ autoFocus = false, modal = false, onClose = () => {} }) => {
  const { user } = useAuth();

  const [feedbackText, setFeedbackText] = useState('');
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState('general');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const textareaRef = useRef(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!feedbackText.trim()) {
      setMessage({ type: 'error', text: 'Please enter your feedback.' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    const payload = {
      feedback_text: feedbackText.trim(),
      rating,
      feedback_type: feedbackType,
    };

    if (user?.uid) payload.user_id = user.uid;
    if (email) payload.email = email.trim();
    else if (user?.email) payload.email = user.email;

    try {
      const resp = await apiService.submitFeedback(payload);

      if (resp?.success) {
        setMessage({ type: 'success', text: 'Thanks! Your feedback has been submitted.' });
        trackFeedbackSubmit(rating, feedbackText.trim().length > 0);
        setFeedbackText('');
        setRating(5);
        setFeedbackType('general');
        setEmail('');
      } else {
        setMessage({ type: 'error', text: resp?.message || 'Failed to submit feedback.' });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const Star = ({ filled }) => (
    <svg
      className={`w-7 h-7 transition ${
        filled ? 'text-yellow-400' : 'text-gray-300'
      }`}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M12 .587l3.668 7.431L24 9.748l-6 5.847L19.335 24 12 20.201 4.665 24 6 15.595 0 9.748l8.332-1.73L12 .587z" />
    </svg>
  );

  const formContent = (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {/* Feedback Text */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          Your Feedback
        </label>
        <textarea
          ref={textareaRef}
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          rows={6}
          placeholder={
            "Tell us what worked well, what didn’t, or what you'd love to see next…"
          }
          className={
            "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm shadow-sm transition focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/60"
          }
          required
        />
      </div>

      {/* Rating / Type / Email */}
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rating
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="
                    p-1 transition-transform
                    hover:scale-110 active:scale-95
                    focus:outline-none
                  "
                  aria-label={`Rate ${n} star`}
                >
                  <Star filled={n <= (hoverRating || rating)} />
                </button>
              ))}
              {/* numeric rating label removed to avoid wrapping under other fields on small screens */}
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <select
              value={feedbackType}
              onChange={(e) => setFeedbackType(e.target.value)}
              className={
                "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary/50"
              }
            >
              <option value="general">General</option>
              <option value="bug">Bug</option>
              <option value="suggestion">Suggestion</option>
              <option value="content">Content</option>
            </select>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email (optional)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={"Optional — if you'd like a reply"}
              className={
                "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/50"
              }
            />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          className={
            "inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold bg-gradient-to-r from-primary to-purple-600 text-white rounded-full shadow-md transition hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          }
        >
          {submitting ? 'Sending...' : 'Send Feedback'}
        </button>

        {message && (
          <div
            className={`
              rounded-md px-3 py-2 text-sm
              ${message.type === 'success'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'}
            `}
          >
            {message.text}
          </div>
        )}
      </div>
    </form>
  );

  if (!modal) return formContent;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-white p-8 shadow-2xl border border-gray-100"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
          aria-label="Close"
        >
          ✕
        </button>

        <h3 className="text-2xl font-bold text-gray-900">
          Send Us Feedback
        </h3>
        <p className="text-gray-600 mt-1 mb-4">
          Share your experience — feedback helps us improve Educado AI.
        </p>

        <div className="h-px bg-gray-100 mb-6" />

        {formContent}
      </div>
    </div>
  );
};

export default FeedbackForm;
